/**
 * FPL Pre-Screening Calculator
 * Pure-computation eligibility estimation with no PHI transmitted.
 * County selection uses a dropdown of all 58 California counties.
 */

import React, { useState } from 'react';
import { eligibilityApi, PreScreenResult, EligibleProgram, MCPInfo } from '@/services/api/eligibility';

const CA_COUNTIES = [
  "Alameda", "Alpine", "Amador", "Butte", "Calaveras", "Colusa",
  "Contra Costa", "Del Norte", "El Dorado", "Fresno", "Glenn",
  "Humboldt", "Imperial", "Inyo", "Kern", "Kings", "Lake", "Lassen",
  "Los Angeles", "Madera", "Marin", "Mariposa", "Mendocino", "Merced",
  "Modoc", "Mono", "Monterey", "Napa", "Nevada", "Orange", "Placer",
  "Plumas", "Riverside", "Sacramento", "San Benito", "San Bernardino",
  "San Diego", "San Francisco", "San Joaquin", "San Luis Obispo",
  "San Mateo", "Santa Barbara", "Santa Clara", "Santa Cruz", "Shasta",
  "Sierra", "Siskiyou", "Solano", "Sonoma", "Stanislaus", "Sutter",
  "Tehama", "Trinity", "Tulare", "Tuolumne", "Ventura", "Yolo", "Yuba"
];

const EligibilityPreScreenForm: React.FC = () => {
  const [formData, setFormData] = useState({
    household_size: 1,
    monthly_income: 0,
    age: 30,
    is_pregnant: false,
    is_disabled: false,
    county: '',
    state_code: 'CA',
  });
  const [result, setResult] = useState<PreScreenResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await eligibilityApi.preScreen(formData);
      setResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Pre-screening failed');
    } finally {
      setLoading(false);
    }
  };

  const confidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'HIGH': return 'bg-green-100 text-green-800 border-green-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-[13px] text-ink-500 mb-4">
          Estimate Medi-Cal eligibility based on income and demographics.
          No personal health information is transmitted.
        </p>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Household Size</label>
            <input
              type="number"
              min={1}
              max={20}
              value={formData.household_size}
              onChange={(e) => setFormData({ ...formData, household_size: parseInt(e.target.value) || 1 })}
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Income ($)</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={formData.monthly_income}
              onChange={(e) => setFormData({ ...formData, monthly_income: parseFloat(e.target.value) || 0 })}
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
            <input
              type="number"
              min={0}
              max={150}
              value={formData.age}
              onChange={(e) => setFormData({ ...formData, age: parseInt(e.target.value) || 0 })}
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">California County</label>
            <select
              value={formData.county}
              onChange={(e) => setFormData({ ...formData, county: e.target.value })}
              className="w-full border rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">Select county...</option>
              {CA_COUNTIES.map((county) => (
                <option key={county} value={county}>{county}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-6 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_pregnant}
                onChange={(e) => setFormData({ ...formData, is_pregnant: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm">Pregnant</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_disabled}
                onChange={(e) => setFormData({ ...formData, is_disabled: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm">Disabled</span>
            </label>
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              {loading ? 'Screening...' : 'Screen Eligibility'}
            </button>
          </div>
        </form>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>

      {result && (
        <div className="space-y-4">
          {/* FPL Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Screening Results</h3>
              <span className="text-sm text-gray-500">{result.screening_date}</span>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold text-blue-600">{result.fpl_percentage}%</div>
                <div className="text-xs text-gray-500">of Federal Poverty Level</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold">${result.annual_income.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Annual Income</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded">
                <div className="text-2xl font-bold">{result.eligible_programs.length}</div>
                <div className="text-xs text-gray-500">Eligible Program(s)</div>
              </div>
            </div>
          </div>

          {/* Eligible Programs */}
          {result.eligible_programs.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-3">Likely Eligible Programs</h3>
              <div className="space-y-3">
                {result.eligible_programs.map((prog: EligibleProgram, idx: number) => (
                  <div key={idx} className={`p-3 border rounded-md ${confidenceColor(prog.confidence)}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{prog.program_name}</span>
                      <span className="text-xs px-2 py-1 rounded-full bg-white/50">
                        {prog.confidence} confidence
                      </span>
                    </div>
                    <div className="text-xs mt-1">
                      FPL threshold: {prog.fpl_threshold_pct}% | Category: {prog.category}
                      {prog.aid_code_examples.length > 0 && (
                        <> | Aid codes: {prog.aid_code_examples.join(', ')}</>
                      )}
                    </div>
                    {prog.notes && <div className="text-xs mt-1 italic">{prog.notes}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* County MCPs */}
          {result.county_mcps.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-3">Managed Care Plans in {formData.county} County</h3>
              <div className="space-y-2">
                {result.county_mcps.map((mcp: MCPInfo, idx: number) => (
                  <div key={idx} className="flex items-center justify-between p-2 border rounded">
                    <div>
                      <span className="font-medium text-sm">{mcp.plan_name}</span>
                      <span className="ml-2 text-xs text-gray-500">({mcp.mcp_model})</span>
                    </div>
                    {mcp.is_default_assignment && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        Default
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {result.recommended_actions.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">Recommended Actions</h4>
              <ul className="space-y-1">
                {result.recommended_actions.map((action: string, idx: number) => (
                  <li key={idx} className="text-sm text-blue-700">- {action}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EligibilityPreScreenForm;
